import { DATE_TIME_FORMAT_DEFAULT, DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';

import UsersService from 'api/users/UsersService';
import ListingTable from 'common/components/listingTable/ListingTable';
import ListingTableCache, { ListingTableData } from 'common/components/listingTable/ListingTableCache';
import TableActionButton from 'common/components/listingTable/actionButton/TableActionButton';
import { OrderData, ListingTableColumn, FilterData } from 'common/components/listingTable/models';
import ListingTableSidebar from 'common/components/listingTable/sidebar/ListingTableSidebar';
import { orderColumns } from 'common/components/listingTable/tableUtils';
import ConfirmDeleteModal from 'common/components/modal/confirmDeleteModal/ConfirmDeleteModal';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageContainer from 'common/components/pageContainer/PageContainer';
import PageHeader from 'common/components/pageHeader/PageHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import Loading from 'common/services/Loading';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import Logger from 'common/services/Logger';
import { Reducers } from 'store/types';
import Toast from 'common/services/Toast';
import Button from 'common/components/button/Button';
import DateFormat from 'common/components/dateFormat/dateFormat';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';

import { BudgetGeneralityDefaultDto } from 'api/budgetGeneralityDefaults/models/BudgetGeneralityDefaultDto';
import { BudgetGeneralityDefaultSearchCriteria } from 'api/budgetGeneralityDefaults/models/BudgetGeneralityDefaultSearchCriteria';
import BudgetGeneralityDefaultsService from 'api/budgetGeneralityDefaults/BudgetGeneralityDefaultsService';
import UnitsService from 'api/units/UnitsService';
import { UnitDto } from 'api/units/models/UnitDto';
import { SelectInputOption } from 'common/components/selectInput/SelectInput';

const PAGE_ID = 'BACKOFFICE_BUDGET_GENERALITY_DEFAULTS_LIST';
const baseUrl = '/backoffice/budget-generality-defaults';

function BudgetGeneralityDefaultsListScreen(): JSX.Element | null {
    const { t, i18n } = useTranslation();
    const [tableData, setTableData] = useState<ListingTableData<BudgetGeneralityDefaultDto, Partial<BudgetGeneralityDefaultSearchCriteria>>>(ListingTableCache.get(PAGE_ID, {
        order: { field: 'order', orderColumn: 'bgd."order"', isOrderAsc: true },
        hiddenColumns: [],
        columnsOrder: [],
        columnsWidth: [],
        page: 1,
        filters: {},
        lastRowId: null,
    }));
    const [items, setItems] = useState<BudgetGeneralityDefaultDto[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [toDelete, setToDelete] = useState<BudgetGeneralityDefaultDto | null>(null);
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = UsersService.hasPolicies(loggedUser?.policies ?? [], ['BUDGET_GENERALITY_DEFAULTS_WRITE']);
    const canRead = UsersService.hasPolicies(loggedUser?.policies ?? [], ['BUDGET_GENERALITY_DEFAULTS_READ']);
    const [scrollToRowId, setScrollToRowId] = useState<string | null>(null);
    const firstLoad = useRef(true);
    const [refreshKey, setRefreshKey] = useState(0);
    const [units, setUnits] = useState<SelectInputOption[]>();

    const columns: ListingTableColumn<BudgetGeneralityDefaultDto>[] = orderColumns([
        {
            name: t('screens.budget_generality_defaults.header.order'),
            field: 'order',
            minWidth: '6rem',
            orderColumn: 'bgd."order"',
        },
        {
            name: t('screens.budget_generality_defaults.header.designation'),
            field: 'designation',
            filter: { type: 'text', value: tableData.filters.designation ?? '', placeholder: t('screens.budget_generality_defaults.header.designation') },
            minWidth: '24rem',
        },
        {
            name: t('screens.budget_generality_defaults.header.unit'),
            field: 'unitSymbol',
            minWidth: '8rem',
            orderColumn: 'u.symbol',
            filter: { type: 'dropdown', value: tableData.filters.unitId ?? '', options: units ?? [] },
        },
        {
            name: t('screens.budget_generality_defaults.header.unit_price'),
            field: 'unitPrice',
            minWidth: '12rem',
            orderColumn: 'bgd.unit_price',
            renderCell: (row) => <MoneyFormat value={row.unitPrice} />,
        },
        {
            name: t('common.updated_date'),
            field: 'updatedDate',
            filter: {
                type: 'date-range',
                value: {
                    start: tableData.filters.updatedDate?.start ? new Date(tableData.filters.updatedDate.start) : null,
                    end: tableData.filters.updatedDate?.end ? new Date(tableData.filters.updatedDate.end) : null,
                },
            },
            minWidth: '20rem',
            orderColumn: 'updated_date',
            renderCell: (row) => row.updatedDate ? <DateFormat value={row.updatedDate} format={DATE_TIME_FORMAT_DEFAULT} /> : '',
        },
        {
            name: t('common.user_updated'),
            field: 'userUpdatedName',
            filter: { type: 'text', value: tableData.filters.userUpdatedName ?? '', placeholder: t('common.user_updated') },
            minWidth: '18rem',
            orderColumn: 'user_updated_name',
        },
    ], tableData.columnsOrder);

    const renderAction = (row: BudgetGeneralityDefaultDto) => (
        <TableActionButton
            options={[
                { label: <span><FaEdit /> {t('common.edit')}</span>, href: `${baseUrl}/edit/${row.id}`, onClick: () => ListingTableCache.saveLastRowId(PAGE_ID, row.id) },
                { label: <span><FaTrash /> {t('common.delete')}</span>, onClick: () => onOpenModalToDelete(row) },
            ]}
        />
    );

    const onFilter = (col: ListingTableColumn<BudgetGeneralityDefaultDto>, filterData: FilterData) => {
        setTableData(d => {
            const data = ({ ...d, page: 1 });
            if (col.field === 'designation') {
                data.filters = { ...data.filters, designation: filterData.value as string };
            }
            if (col.field === 'unitSymbol') {
                data.filters = { ...data.filters, unitId: filterData.value as string };
            }
            if (col.field === 'updatedDate') {
                data.filters = {
                    ...data.filters,
                    updatedDate: filterData.value,
                    updatedDateStart: filterData.value?.start as Date,
                    updatedDateEnd: filterData.value?.end as Date,
                };
            }
            if (col.field === 'userUpdatedName') {
                data.filters = { ...data.filters, userUpdatedName: filterData.value as string };
            }
            return data;
        });
        setRefreshKey(k => k + 1);
    };

    const onClearFilters = () => {
        setTableData(d => ({ ...d, page: 1, filters: {}, lastRowId: null }));
        setRefreshKey(k => k + 1);
    };

    const onOrder = (orderData: OrderData<BudgetGeneralityDefaultDto>) => {
        setRefreshKey(k => k + 1);
        setTableData(d => ({ ...d, order: orderData, page: 1 }));
    };

    useEffect(() => {
        void getData();
    }, [tableData.page, tableData.filters, tableData.order, refreshKey]);

    useEffect(() => {
        ListingTableCache.save(PAGE_ID, tableData);
    }, [tableData]);

    const getData = async () => {
        try {
            if (isLoading) {
                return;
            }
            Loading.show();
            setIsLoading(true);

            const data = await BudgetGeneralityDefaultsService.getList({
                ...tableData.filters,
                languageIso: i18n.language,
                page: firstLoad.current && tableData.page > 1 ? 1 : tableData.page,
                itemsPerPage: firstLoad.current && tableData.page > 1 ? DEFAULT_PAGINATION_ITEMS_PER_PAGE * tableData.page : DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                orderColumn: tableData.order.orderColumn,
                orderBy: tableData.order.isOrderAsc ? 'asc' : 'desc',
            });

            const newListElements = data.currentPage === 1 ? data.items : [...items, ...data.items];
            setHasMore(data.totalItems > newListElements.length);
            setItems(newListElements);
            setTotalItems(data.totalItems);

            if (firstLoad.current) {
                const unitsData = await UnitsService.getFormattedCatalog();
                setUnits(unitsData.map((x: UnitDto) => ({
                    label: x.name,
                    value: x.id,
                })));
                setScrollToRowId(tableData.lastRowId);
            }
            firstLoad.current = false;
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get budget generality defaults list', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
            setIsLoading(false);
        }
    };

    const onRowClick = (row: BudgetGeneralityDefaultDto) => {
        ListingTableCache.saveLastRowId(PAGE_ID, row.id);
    };

    const onLoadMore = () => {
        setTableData(d => ({ ...d, page: d.page + 1 }));
    };
    const onOpenModalToDelete = (row: BudgetGeneralityDefaultDto) => {
        setToDelete(row);
        setShowConfirmDeleteModal(true);
    };

    const onDelete = async (result: boolean) => {
        if (!result) {
            setShowConfirmDeleteModal(false);
            return;
        }
        try {
            await BudgetGeneralityDefaultsService.remove(toDelete as BudgetGeneralityDefaultDto);
            setTableData(d => ({ ...d, page: 1 }));
            setRefreshKey(k => k + 1);
            Loading.hide();
            Toast.success(t('messages.record_delete_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete budget generality default', error);
            Toast.error(t('messages.record_delete_error'));
        }
        setShowConfirmDeleteModal(false);
    };

    if (!canRead) {
        return null;
    }

    return (
        <ScreenTitle title={t('budget_generality_defaults.title')}>
            <PageBreadcrumbsPortal
                breadcrumbs={[
                    { name: t('home.title'), url: '/' },
                    { name: t('budget_generality_defaults.title'), url: baseUrl },
                ]}
            />
            <PageHeader title={t('budget_generality_defaults.title')} subTitle={`${totalItems} ${t('common.total_results')}`} addSidebarSpacing informationText={t('common.list')} >
                {canWrite && <Button href={`${baseUrl}/create`}>{t('common.new')}</Button>}
            </PageHeader>
            <PageContainer>
                <ListingTable<BudgetGeneralityDefaultDto>
                    columns={columns}
                    rows={items}
                    order={tableData.order}
                    hiddenColumns={tableData.hiddenColumns}
                    columnsWidths={tableData.columnsWidth}
                    onOrder={onOrder}
                    onFilter={onFilter}
                    onClearFilters={onClearFilters}
                    onRenderAction={canWrite ? renderAction : undefined}
                    onRowClick={onRowClick}
                    onHref={(row) => `${baseUrl}/details/${row.id}`}
                    infiniteScroll={{
                        isLoading,
                        hasMore,
                        onLoadMore,
                    }}
                    onRowId={r => r.id}
                    scrollToRowId={scrollToRowId}
                    onColResize={(w) => setTableData(d => ({ ...d, columnsWidth: w }))}
                />
            </PageContainer>
            <ListingTableSidebar
                columns={columns}
                hiddenColumns={tableData.hiddenColumns}
                onChangeHiddenColumns={(hc) => setTableData(d => ({ ...d, hiddenColumns: hc }))}
                onChangeColumnsOrder={(oc) => setTableData(d => ({ ...d, columnsOrder: oc }))}
            />
            <ConfirmDeleteModal
                itemName={toDelete?.designation}
                isOpen={showConfirmDeleteModal}
                onClose={(result) => onDelete(result)}
            />
        </ScreenTitle>
    );
}

export default BudgetGeneralityDefaultsListScreen;
